import type {MensajeDelAsistente} from '@cnc/contracts';
import {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router';
import {useEstadoDelAsistente, usePreguntarAlAsistente} from '../api/asistente';
import {claveDeTexto, tr, trx, txt} from '../i18n/idioma';

const SUGERENCIAS = [
  txt('¿Cómo veo cuánto oro se exportó por mes?', 'How do I see how much gold was exported per month?'),
  txt('¿Cómo guardo mis filtros para usarlos cada semana?', 'How do I save my filters to use them every week?'),
  txt('¿Qué significa Asignado en el inventario?', 'What does Assigned mean in the inventory?'),
];

// Boton "Ayuda" del menu lateral con el asistente: responde como usar la
// plataforma y sugiere como armar reportes. Va en el menu (no flotando) para
// no tapar botones de las pantallas; la ventana se minimiza sin perder la
// conversacion. La conversacion vive solo en esta pantalla (nada se guarda en
// el navegador ni en el servidor).
export function AsistenteDeAyuda({contraido = false}: {contraido?: boolean}) {
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState<MensajeDelAsistente[]>([]);
  const [texto, setTexto] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const {pathname} = useLocation();
  const estado = useEstadoDelAsistente(abierto);
  const preguntar = usePreguntarAlAsistente();
  const final = useRef<HTMLDivElement>(null);

  useEffect(() => {
    final.current?.scrollIntoView?.({block: 'end'});
  }, [mensajes, preguntar.isPending]);

  const enviar = (pregunta: string) => {
    const limpia = pregunta.trim();
    if (!limpia || preguntar.isPending) return;
    const nueva: MensajeDelAsistente = {rol: 'persona', texto: limpia.slice(0, 4000)};
    const conversacion = [...mensajes, nueva].slice(-30);
    setMensajes(conversacion);
    setTexto('');
    setAviso(null);
    preguntar.mutate(
      {mensajes: conversacion, pantalla: pathname},
      {
        onSuccess: r => {
          if (r.respuesta) setMensajes(actual => [...actual, {rol: 'asistente', texto: r.respuesta!}]);
          else setAviso(r.aviso);
        },
        onError: e => setAviso(e.message),
      },
    );
  };

  const disponible = estado.data?.disponible;

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(v => !v)}
        aria-expanded={abierto}
        title={
          mensajes.length && !abierto
            ? tr('Ayuda (conversación minimizada)', 'Help (minimized conversation)')
            : tr('Asistente de ayuda', 'Help assistant')
        }
        className={`relative flex w-full items-center gap-3 rounded-md py-2 text-sm hover:bg-papel ${
          abierto ? 'bg-verde-claro font-medium text-verde-oscuro' : 'text-tinta'
        } ${contraido ? 'justify-center px-2' : 'px-3'}`}
      >
        <span aria-hidden className="w-5 text-center font-semibold text-verde-oscuro">
          ?
        </span>
        <span className="sr-only">? </span>
        <span className={contraido ? 'sr-only' : ''}>{tr('Ayuda', 'Help')}</span>
        {mensajes.length > 0 && !abierto && (
          <span
            aria-hidden
            className={`h-2 w-2 rounded-full bg-verde ${contraido ? 'absolute top-1 right-1' : 'ml-auto'}`}
          />
        )}
      </button>
      {abierto && (
        <section
          role="dialog"
          aria-label={tr('Asistente de ayuda', 'Help assistant')}
          className="fixed right-6 bottom-6 z-40 flex h-[32rem] max-h-[calc(100vh-3rem)] w-[26rem] max-w-[calc(100vw-2rem)] flex-col rounded-xl border border-linea bg-white shadow-2xl"
        >
          <header className="flex items-center justify-between border-b border-linea px-4 py-3">
            <div>
              <div className="font-semibold text-verde-oscuro">{tr('Asistente de ayuda', 'Help assistant')}</div>
              <div className="text-xs text-apagado">
                {tr('Cómo usar la plataforma y armar tus reportes', 'How to use the platform and build your reports')}
              </div>
            </div>
            <div className="flex gap-2">
              {mensajes.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setMensajes([]);
                    setAviso(null);
                  }}
                  className="text-xs text-apagado hover:text-tinta"
                >
                  {tr('Nueva conversación', 'New conversation')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setAbierto(false)}
                aria-label={tr('Minimizar', 'Minimize')}
                title={tr(
                  'Minimizar: la conversación sigue en "? Ayuda" del menú',
                  'Minimize: the conversation stays in "? Help" in the menu',
                )}
                className="px-1 text-lg leading-none text-apagado hover:text-tinta"
              >
                –
              </button>
              <button
                type="button"
                onClick={() => {
                  setAbierto(false);
                  setMensajes([]);
                  setAviso(null);
                }}
                aria-label={tr('Cerrar', 'Close')}
                title={tr('Cerrar y borrar la conversación', 'Close and clear the conversation')}
                className="px-1 text-lg leading-none text-apagado hover:text-tinta"
              >
                ×
              </button>
            </div>
          </header>
          <div className="flex-1 space-y-3 overflow-auto px-4 py-3 text-sm">
            {disponible === false ? (
              <p className="rounded-md bg-papel p-3 text-apagado">
                {tr(
                  'El asistente de ayuda todavía no está configurado. Un administrador debe activarlo.',
                  'The help assistant is not set up yet. An administrator must turn it on.',
                )}
              </p>
            ) : (
              <>
                {mensajes.length === 0 && (
                  <div className="space-y-2">
                    <p className="text-apagado">
                      {tr(
                        'Pregúntame cómo funciona una pantalla, qué significa un campo o cómo organizar los datos para un reporte. No veo tus datos: te digo dónde encontrarlos.',
                        'Ask me how a screen works, what a field means or how to organize the data for a report. I cannot see your data: I tell you where to find it.',
                      )}
                    </p>
                    {SUGERENCIAS.map(s => (
                      <button
                        key={claveDeTexto(s)}
                        type="button"
                        onClick={() => enviar(trx(s))}
                        className="block w-full rounded-md border border-linea px-3 py-2 text-left hover:bg-papel"
                      >
                        {trx(s)}
                      </button>
                    ))}
                  </div>
                )}
                {mensajes.map((m, i) => (
                  <div
                    key={i}
                    className={
                      m.rol === 'persona'
                        ? 'ml-8 rounded-lg bg-verde-claro px-3 py-2 whitespace-pre-wrap text-verde-oscuro'
                        : 'mr-4 rounded-lg bg-papel px-3 py-2 whitespace-pre-wrap text-tinta'
                    }
                  >
                    {m.texto}
                  </div>
                ))}
                {preguntar.isPending && <div className="text-apagado">{tr('Pensando…', 'Thinking…')}</div>}
                {aviso && (
                  <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-amber-900">
                    {aviso}
                  </p>
                )}
              </>
            )}
            <div ref={final} />
          </div>
          {disponible !== false && (
            <form
              className="border-t border-linea p-3"
              onSubmit={e => {
                e.preventDefault();
                enviar(texto);
              }}
            >
              <div className="flex gap-2">
                <textarea
                  value={texto}
                  onChange={e => setTexto(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      enviar(texto);
                    }
                  }}
                  rows={2}
                  maxLength={4000}
                  aria-label={tr('Tu pregunta', 'Your question')}
                  placeholder={tr('Escribe tu pregunta…', 'Type your question…')}
                  className="flex-1 resize-none rounded-md border border-linea px-2 py-1.5 text-sm"
                />
                <button
                  type="submit"
                  disabled={!texto.trim() || preguntar.isPending}
                  className="self-end rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  {tr('Enviar', 'Send')}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-apagado">
                {tr(
                  'No escribas contraseñas ni datos personales. Las respuestas pueden tener errores: confírmalas en la pantalla.',
                  'Do not type passwords or personal data. Answers may contain mistakes: check them on screen.',
                )}
              </p>
            </form>
          )}
        </section>
      )}
    </>
  );
}
